/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { compressToBase64, decompressFromBase64 } from 'lz-string';
import hash from 'object-hash';
import type { EditorType } from '@ckeditor/ckeditor5-metadata';
import { plugins } from '../../constants/plugins';
import pkgJson from '../../../package.json';
import type { EditorPlugins } from '../../types/EditorPlugins';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import type { EditorTypeId, LocationData, LocationSynchronizationType, ToolbarOptionData } from './types';
import { getPresets, type PresetView } from '../../utils/getPresets/getPresets';
import { type SectionTab, tabs } from '../../constants/tabs';
import type { EditorLanguage } from '../../components/LanguageSelection/languages';
import {
	mapPreviousCapabilitiesByStateFromUrl
} from '../../utils/mapPreviousCapabilitiesByStateFromUrl/mapPreviousCapabilitiesByStateFromUrl';

export function useLocationSynchronization( {
	selectedPreset,
	editorPlugins,
	editorLanguage,
	editorType,
	toolbarOptions,
	activeTabIndex,
	onEditorLanguageChange,
	onToolbarOptionsChange,
	onEditorTypeChange,
	onEditorPluginsChange,
	onPresetChange,
	onIsSelectedPresetModifiedChange,
	onActiveTabIndexChange,
	onLocalCapabilitiesChange,
	onBuilderUrlChange
}: LocationSynchronizationType ): void {
	const callbackForLoadingDataFromHash = useCallback( () => {
		const url = new URL( location.toString() );

		if ( !url.hash ) {
			resetApplicationState();

			return;
		}

		try {
			const [
				activeTab,
				preset,
				,
				editorPlugins,
				editorLanguage,
				editorType,
				toolbarOptions
			] = extractBuilderParametersFromUrl( url );

			onPresetChange( preset );
			onEditorPluginsChange( editorPlugins );
			onEditorLanguageChange( editorLanguage );
			onEditorTypeChange( editorType );
			onToolbarOptionsChange( toolbarOptions );
			onIsSelectedPresetModifiedChange( true );
			onActiveTabIndexChange( activeTab.index );
			onLocalCapabilitiesChange( previousCapabilities => {
				return mapPreviousCapabilitiesByStateFromUrl( previousCapabilities, editorPlugins );
			} );
		} catch ( error ) {
			resetApplicationState();

			url.hash = '';
			history.replaceState( {}, '', url );
			onBuilderUrlChange( url.toString() );
		}
	}, [] ); // eslint-disable-line react-hooks/exhaustive-deps

	// To avoid calculating the hash when navigation over the application, let's cache it to improve the performance.
	const hashFromConfiguration = useMemo( () => {
		if ( !selectedPreset ) {
			return null;
		}

		const dataToHash = [
			selectedPreset!.id,
			pkgJson.version,
			pluginsToBitmask( editorPlugins ).toString(),
			editorLanguage,
			mapEditorTypeToNumber( editorType ),
			mapToolbarOptionsToArray( toolbarOptions )
		] as const;

		const data: LocationData = [
			...dataToHash,
			hash( dataToHash ).toString().substring( 0, 7 )
		];

		return JSON.stringify( data );
	}, [ selectedPreset, editorPlugins, editorLanguage, editorType, toolbarOptions ] );

	// Store a new URL combination whenever a user changes builder settings.
	useEffect( () => {
		if ( !hashFromConfiguration ) {
			return;
		}

		const activeTab = tabs.find( tab => activeTabIndex === tab.index )!;
		const url = new URL( location.toString() );

		url.hash = [
			activeTab.slug,
			compressToBase64( hashFromConfiguration )
		].join( '/' );

		onBuilderUrlChange( url.toString() );

		// Do not store the same hash to avoid overriding the "Next" stack.
		if ( location.hash !== url.hash ) {
			history.pushState( {}, '', url );
		}
	}, [ hashFromConfiguration, activeTabIndex, onBuilderUrlChange ] );

	// Restore the application state when a user entries.
	useEffect( () => {
		callbackForLoadingDataFromHash();
	}, [ callbackForLoadingDataFromHash ] );

	// Observe the "Back" and "Next" browser's actions.
	useEffect( () => {
		const callback = () => callbackForLoadingDataFromHash();

		window.addEventListener( 'popstate', callback );

		return () => {
			window.removeEventListener( 'popstate', callback );
		};
	}, [ callbackForLoadingDataFromHash ] );

	function resetApplicationState() {
		onActiveTabIndexChange( 0 );
		onPresetChange( null );
		onIsSelectedPresetModifiedChange( false );
	}
}

type DataFromUrl = [ SectionTab, PresetView, string, EditorPlugins, EditorLanguage, EditorType, ToolbarOptions ];

function extractBuilderParametersFromUrl( url: URL ): DataFromUrl {
	const [ tabSlug, ...hashedState ] = url.hash.slice( 1 ).split( '/' );
	const newActiveTab = tabs.find( tab => tabSlug === tab.slug );
	const urlHash = hashedState.join( '/' );

	if ( !newActiveTab ) {
		throw new Error( 'Invalid active tab slug' );
	}

	const hashData: LocationData = JSON.parse( decompressFromBase64( urlHash ) );
	const controlSum = hashData.pop();
	const expectedControlSum = hash( hashData ).toString().substring( 0, 7 );
	const expectedUrlHash = compressToBase64( JSON.stringify( [ ...hashData, expectedControlSum ] ) );

	if ( controlSum !== expectedControlSum ) {
		throw new Error( 'Invalid control sum' );
	}

	if ( expectedUrlHash !== urlHash ) {
		throw new Error( 'Invalid base64 url' );
	}

	const [
		presetId,
		version,
		pluginBitmaskString,
		editorLanguage,
		editorTypeId,
		toolbarOptionsData
	] = hashData;

	const pluginBitmask = BigInt( pluginBitmaskString );

	return [
		newActiveTab,
		getPresets().find( preset => preset.id === presetId )!,
		version,
		pluginBitmaskToNames( pluginBitmask ),
		editorLanguage,
		mapEditorTypeIdToType( editorTypeId ),
		mapToolbarOptionsArrayToObject( toolbarOptionsData )
	];
}

function mapEditorTypeToNumber( editorType: EditorType ): EditorTypeId {
	const editorTypeToEditorTypeIdMap: Record<EditorType, EditorTypeId> = {
		'ClassicEditor': 0,
		'InlineEditor': 1,
		'BalloonEditor': 2,
		'DecoupledEditor': 3
	};

	return editorTypeToEditorTypeIdMap[ editorType ];
}

function mapEditorTypeIdToType( editorTypeId: EditorTypeId ): EditorType {
	const editorTypeIdToEditorTypeMap: Record<EditorTypeId, EditorType> = {
		0: 'ClassicEditor',
		1: 'InlineEditor',
		2: 'BalloonEditor',
		3: 'DecoupledEditor'
	};

	return editorTypeIdToEditorTypeMap[ editorTypeId ];
}

function mapToolbarOptionsToArray( toolbarOptions: ToolbarOptions ): ToolbarOptionData {
	return [
		toolbarOptions.isMainToolbarOn ? 1 : 0,
		toolbarOptions.isBalloonToolbarOn ? 1 : 0,
		toolbarOptions.isBlockToolbarOn ? 1 : 0,
		toolbarOptions.isCollapsable ? 1 : 0,
		toolbarOptions.isMenuBarUsed ? 1 : 0,
		toolbarOptions.buttonsOptions === 'essential' ? 0 : 1
	];
}

function mapToolbarOptionsArrayToObject( toolbarOptionsData: ToolbarOptionData ): ToolbarOptions {
	const [
		isMainToolbarOn,
		isBalloonToolbarOn,
		isBlockToolbarOn,
		isCollapsable,
		isMenuBarUsed,
		buttonsOptions
	] = toolbarOptionsData;

	return {
		isMainToolbarOn: Boolean( isMainToolbarOn ),
		isBalloonToolbarOn: Boolean( isBalloonToolbarOn ),
		isBlockToolbarOn: Boolean( isBlockToolbarOn ),
		isCollapsable: Boolean( isCollapsable ),
		isMenuBarUsed: Boolean( isMenuBarUsed ),
		buttonsOptions: buttonsOptions === 0 ? 'essential' : 'all'
	};
}

function pluginsToBitmask( editorPlugins: EditorPlugins ): bigint {
	return editorPlugins
		.map( pluginName => {
			return plugins.find( item => item.className === pluginName )!.id;
		} )
		.reduce( ( output, current ) => {
			return output | BigInt( 1 ) << BigInt( current );
		}, BigInt( 0 ) );
}

function pluginBitmaskToNames( pluginBitmask: bigint ): EditorPlugins {
	return plugins
		.filter( plugin => {
			const pluginMask = BigInt( 1 ) << BigInt( plugin.id );

			return ( pluginBitmask & pluginMask ) === pluginMask;
		} )
		.map( plugin => plugin.className );
}
